import { audioRecordingModule } from "@/modules/audio-recording";
import { isDesktopAudioArtifactMissingOrEmpty } from "@/modules/electron/desktop-audio-artifact-ref";
import { electronModule } from "@/modules/electron";
import type {
  DesktopAudioArtifactRef,
  DesktopAudioArtifactUploadFailureDebugInfo,
} from "@/modules/electron/types";
import { logger } from "@/modules/logger";
import { uuidModule } from "@/modules/uuid";
import { AudioRecordingRepository } from "@/domains/dexie-db/repositories/audio-recording/audio-recording";
import { LocalAudioRecordingTranscriptTurnsRepository } from "@/domains/dexie-db/repositories/audio-recording/local-audio-recording-transcript-turns";
import type { AudioUploadFailureDebugInfo } from "@mem-client/uploads";
import { AudioUploadFailureStage } from "@mem-client/uploads";
import { buildAudioRecordingArtifactDiagnostics } from "@/store/audio/audio-recording-artifact-diagnostics";
import {
  AudioRecordingRef,
  AudioUploadPayloadProvider,
  PreparedAudioUploadResult,
} from "@/store/audio/uploading/audio-uploading.types";

type DesktopAudioUploadPayloadProviderConfig = {
  recordings: AudioRecordingRepository;
  transcripts: LocalAudioRecordingTranscriptTurnsRepository;
};

export class DesktopAudioUploadPayloadProvider
  implements AudioUploadPayloadProvider<DesktopAudioArtifactRef>
{
  private readonly recordings: AudioRecordingRepository;
  private readonly transcripts: LocalAudioRecordingTranscriptTurnsRepository;

  constructor({ recordings, transcripts }: DesktopAudioUploadPayloadProviderConfig) {
    this.recordings = recordings;
    this.transcripts = transcripts;
  }

  async prepareUploadPayload({
    audioRecordingId,
  }: AudioRecordingRef): Promise<PreparedAudioUploadResult<DesktopAudioArtifactRef>> {
    const recording = await this.recordings.getById({ id: audioRecordingId });
    if (!recording) {
      return {
        success: false,
        error: "Recording not found",
        shouldPersistError: false,
      };
    }

    let artifactRef: DesktopAudioArtifactRef | null = null;
    let artifactLookupErrorMessage: string | undefined;

    try {
      artifactRef = await electronModule.audioArtifacts.getAudioArtifactRef({
        audioRecordingId,
      });
    } catch (error) {
      artifactLookupErrorMessage = this.toErrorMessage({ error });
    }

    if (artifactLookupErrorMessage || isDesktopAudioArtifactMissingOrEmpty({ artifactRef })) {
      const debugInfo = await this.getUploadFailureDebugInfo({
        audioRecordingId,
        diagnosticErrorMessage: artifactLookupErrorMessage,
      });

      const diagnostics = buildAudioRecordingArtifactDiagnostics({
        diagnosticsSource: "desktop-upload-payload-provider",
        audioRecordingId,
        modelData: recording.data,
        artifactAvailability: artifactRef === null ? "missing" : "empty",
        artifactSizeBytes: artifactRef?.sizeBytes ?? debugInfo?.artifactSizeBytes ?? null,
        artifactDurationMs: artifactRef?.durationMs ?? null,
        artifactPartCount: artifactRef ? 1 : 0,
        artifactMimeType: artifactRef?.mimeType ?? null,
        hasUploadableArtifact: artifactRef !== null && artifactRef.sizeBytes > 0,
        extraInfo: {
          uploadFailureStage: AudioUploadFailureStage.PrepareFailed,
          artifactFailureReason: debugInfo?.artifactFailureReason ?? null,
          artifactFilePath: debugInfo?.artifactFilePath ?? null,
          manifestPath: debugInfo?.manifestPath ?? null,
          artifactDirectoryPath: debugInfo?.artifactDirectoryPath ?? null,
          artifactState: debugInfo?.artifactState ?? null,
          appendCount: debugInfo?.appendCount ?? null,
          appendFailureCount: debugInfo?.appendFailureCount ?? null,
          bytesWritten: debugInfo?.bytesWritten ?? null,
          createdAt: debugInfo?.createdAt ?? null,
          updatedAt: debugInfo?.updatedAt ?? null,
          firstAppendAt: debugInfo?.firstAppendAt ?? null,
          lastAppendAt: debugInfo?.lastAppendAt ?? null,
          finalizedAt: debugInfo?.finalizedAt ?? null,
          artifactRefPresent: debugInfo?.artifactRefPresent ?? null,
          artifactSnapshotPresent: debugInfo?.artifactSnapshotPresent ?? null,
          removeReason: debugInfo?.removeReason ?? null,
          removedAt: debugInfo?.removedAt ?? null,
          diagnosticErrorMessage:
            debugInfo?.diagnosticErrorMessage ?? artifactLookupErrorMessage ?? null,
        },
      });

      if (diagnostics.isTooShort === true) {
        logger.info({
          message:
            "[DesktopAudioUploadPayloadProvider] finalized audio artifact is unavailable after a too-short recording",
          info: diagnostics,
        });
      } else {
        logger.warn({
          message: "[DesktopAudioUploadPayloadProvider] finalized audio artifact is unavailable",
          info: diagnostics,
        });
      }

      return {
        success: false,
        error: diagnostics.expectedUserErrorMessage,
        uploadStage: AudioUploadFailureStage.PrepareFailed,
        debugInfo,
      };
    }

    const transcriptRecords = await this.transcripts.getAllByRecordingId({
      associatedAudioRecordingId: audioRecordingId,
    });
    const { transcriptArray, transcriptText } =
      audioRecordingModule.buildTranscriptPayload(transcriptRecords);
    const capturedAudioId = uuidModule.resolveAudioRecordingCapturedAudioUuid({
      associatedAudioRecordingId: audioRecordingId,
    });
    const associatedNoteId = recording.data.associated_note_id ?? null;
    const associatedSessionId = associatedNoteId
      ? uuidModule.resolveCapturedAudioSessionSyncModelUuid({ capturedAudioId })
      : null;
    const finalizedArtifactRef = artifactRef;

    if (finalizedArtifactRef === null) {
      throw new Error("Expected finalized desktop audio artifact");
    }

    return {
      success: true,
      payload: {
        source: finalizedArtifactRef,
        uploadMeta: {
          capturedAudioId,
          associatedAudioRecordingId: audioRecordingId,
          associatedNoteId,
          associatedSessionId,
          preferredMimeType: finalizedArtifactRef.mimeType,
          transcriptArray,
          transcriptText,
        },
      },
    };
  }

  private async getUploadFailureDebugInfo({
    audioRecordingId,
    diagnosticErrorMessage,
  }: {
    audioRecordingId: string;
    diagnosticErrorMessage?: string;
  }): Promise<AudioUploadFailureDebugInfo | undefined> {
    try {
      const debugInfo = await electronModule.audioArtifacts.getAudioArtifactUploadFailureDebugInfo({
        audioRecordingId,
      });

      return this.mapUploadFailureDebugInfo({
        debugInfo,
        diagnosticErrorMessage,
      });
    } catch (error) {
      return this.mapUploadFailureDebugInfo({
        debugInfo: null,
        diagnosticErrorMessage: diagnosticErrorMessage ?? this.toErrorMessage({ error }),
      });
    }
  }

  private mapUploadFailureDebugInfo({
    debugInfo,
    diagnosticErrorMessage,
  }: {
    debugInfo: DesktopAudioArtifactUploadFailureDebugInfo | null;
    diagnosticErrorMessage?: string;
  }): AudioUploadFailureDebugInfo | undefined {
    if (!debugInfo && !diagnosticErrorMessage) {
      return undefined;
    }

    return {
      artifactFailureReason: debugInfo?.reason,
      artifactFilePath: debugInfo?.artifactFilePath,
      manifestPath: debugInfo?.manifestPath,
      artifactDirectoryPath: debugInfo?.artifactDirectoryPath,
      artifactState: debugInfo?.artifactState,
      artifactSizeBytes: debugInfo?.artifactSizeBytes,
      appendCount: debugInfo?.appendCount,
      appendFailureCount: debugInfo?.appendFailureCount,
      bytesWritten: debugInfo?.bytesWritten,
      createdAt: debugInfo?.createdAt,
      updatedAt: debugInfo?.updatedAt,
      firstAppendAt: debugInfo?.firstAppendAt,
      lastAppendAt: debugInfo?.lastAppendAt,
      finalizedAt: debugInfo?.finalizedAt,
      artifactRefPresent: debugInfo?.artifactRefPresent,
      artifactSnapshotPresent: debugInfo?.artifactSnapshotPresent,
      removeReason: debugInfo?.removeReason,
      removedAt: debugInfo?.removedAt,
      diagnosticErrorMessage: debugInfo?.diagnosticErrorMessage ?? diagnosticErrorMessage,
    };
  }

  private readonly toErrorMessage = ({ error }: { error: unknown }): string => {
    if (error instanceof Error) {
      return error.message;
    }

    return String(error);
  };
}
